import { useEffect, useCallback, useRef, useState } from 'react';
import type { Workflow } from '@n8n-builder/n8n-types';
import { createApiClient } from '@n8n-builder/api-client';
import { API_BASE_URL } from '../../lib/api-config';
import { useSettingsStore } from '../../store/settings';
import { useCredentialsStore } from '../../store/credentials';
import { useChatStore } from '../../store/chat';
import { KeyIcon, CheckIcon, SpinnerIcon, ArrowPathIcon } from '../ui/icons';
import { CredentialRequirementCard } from './CredentialRequirementCard';
import { extractCredentialRequirements, applyCredentialsToWorkflow, updateStickyNoteCredentials } from '../../utils/credentials';

interface CredentialsPanelProps {
  workflow: Workflow;
}

export function CredentialsPanel({ workflow }: CredentialsPanelProps) {
  const { activeInstance, openSettingsModal } = useSettingsStore();
  const {
    panelState, errorMessage, availableCredentials, requirements, applied,
    setPanelState, setAvailableCredentials, setRequirements, mapCredential, unmapCredential, setApplied, reset,
  } = useCredentialsStore();
  const { setCurrentWorkflow, currentWorkflowId } = useChatStore();
  const lastWorkflowRef = useRef<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  const fetchCredentials = useCallback(async () => {
    setPanelState('loading');
    try {
      const apiClient = createApiClient(API_BASE_URL);
      const result = await apiClient.listN8nCredentials();
      if (result.error || !result.data?.credentials) {
        setPanelState('error', result.error || 'Failed to fetch credentials');
        return;
      }
      setAvailableCredentials(result.data.credentials);
      setPanelState('ready');
    } catch {
      setPanelState('error', 'Failed to connect to n8n instance');
    }
  }, [setPanelState, setAvailableCredentials]);

  // Extract requirements from workflow and auto-map
  useEffect(() => {
    // Skip re-extraction after applying — the workflow now contains the mapped credentials,
    // which would reset counts. The applied flag resets when the user changes any mapping.
    if (applied) return;

    const workflowKey = JSON.stringify(
      (workflow.nodes || []).map((n) => ({ name: n.name, creds: n.credentials }))
    );

    if (workflowKey === lastWorkflowRef.current) return;
    lastWorkflowRef.current = workflowKey;

    const reqs = extractCredentialRequirements(workflow);

    // Pre-populate mappings from existing credential values in the workflow,
    // but only if the credential ID actually exists in the user's n8n instance
    // (AI generates placeholder IDs like "1" that shouldn't count as mapped)
    for (const node of workflow.nodes || []) {
      if (!node.credentials) continue;
      for (const [credType, credValue] of Object.entries(node.credentials)) {
        const cred = credValue as Record<string, unknown> | undefined;
        if (cred?.id && typeof cred.id === 'string') {
          const isReal = availableCredentials.some((c) => c.id === cred.id);
          if (isReal) {
            const req = reqs.find((r) => r.credentialType === credType);
            if (req && !req.mappedCredential) {
              req.mappedCredential = { id: cred.id as string, name: (cred.name as string) || '' };
            }
          }
        }
      }
    }

    setRequirements(reqs);

  }, [workflow, availableCredentials, setRequirements, mapCredential]);

  // Fetch on mount if we have an instance
  useEffect(() => {
    if (activeInstance && panelState === 'idle') {
      fetchCredentials();
    }
  }, [activeInstance, panelState, fetchCredentials]);

  // Reset when no instance
  useEffect(() => {
    if (!activeInstance) reset();
  }, [activeInstance, reset]);

  // No n8n instance connected
  if (!activeInstance) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100">
          <KeyIcon className="h-6 w-6 text-gray-400" />
        </div>
        <h3 className="text-sm font-medium text-gray-900">No n8n instance connected</h3>
        <p className="mt-1 text-xs text-gray-500">
          Connect your n8n instance to manage credentials
        </p>
        <button
          onClick={() => openSettingsModal('n8n-connection')}
          className="mt-4 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Connect n8n Instance
        </button>
      </div>
    );
  }

  // No credentials needed
  if (requirements.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-green-100">
          <CheckIcon className="h-6 w-6 text-green-600" />
        </div>
        <h3 className="text-sm font-medium text-gray-900">No credentials needed</h3>
        <p className="mt-1 text-xs text-gray-500">
          This workflow doesn't require any credentials
        </p>
      </div>
    );
  }

  const mappedCount = requirements.filter((r) => r.mappedCredential).length;
  const nonOAuth2Unmapped = requirements.filter((r) => !r.mappedCredential && !r.isOAuth2);
  const allNonOAuth2Mapped = nonOAuth2Unmapped.length === 0;

  const handleRemoveCredential = async (credentialType: string) => {
    // Reset the credential to a placeholder so the requirement is still detected
    const updated: Workflow = {
      ...workflow,
      nodes: (workflow.nodes || []).map((node) => {
        if (!node.credentials || !(credentialType in node.credentials)) return node;
        return {
          ...node,
          credentials: { ...node.credentials, [credentialType]: { id: '', name: '' } },
        };
      }),
    };

    // Update sticky note checkboxes — uncheck the removed credential
    const mappedCredTypes = new Set(
      requirements
        .filter((r) => r.mappedCredential && r.credentialType !== credentialType)
        .map((r) => r.credentialType)
    );
    const withStickyUpdated: Workflow = {
      ...updated,
      nodes: (updated.nodes || []).map((node) => {
        if (
          node.type === 'n8n-nodes-base.stickyNote' &&
          typeof node.parameters?.content === 'string' &&
          node.parameters.content.includes('Setup Checklist')
        ) {
          const updatedContent = updateStickyNoteCredentials(node.parameters.content, mappedCredTypes);
          if (updatedContent !== node.parameters.content) {
            return { ...node, parameters: { ...node.parameters, content: updatedContent } };
          }
        }
        return node;
      }),
    };

    setCurrentWorkflow(withStickyUpdated);

    // Persist to R2
    if (currentWorkflowId) {
      try {
        const apiClient = createApiClient(API_BASE_URL);
        await apiClient.updateWorkflow(currentWorkflowId, withStickyUpdated);
      } catch {
        // Silently fail — credential removed in-memory
      }
    }

    // Clear the mapping and reset applied state
    unmapCredential(credentialType);
    setApplied(false);
    lastWorkflowRef.current = null; // Force re-extraction
  };

  const handleApply = async () => {
    setIsApplying(true);
    const updated = applyCredentialsToWorkflow(workflow, requirements);
    setCurrentWorkflow(updated);

    // Persist to R2 so credentials survive conversation switches and page refreshes
    if (currentWorkflowId) {
      try {
        const apiClient = createApiClient(API_BASE_URL);
        await apiClient.updateWorkflow(currentWorkflowId, updated);
      } catch {
        // Silently fail — credentials are still applied in-memory
      }
    }

    setApplied(true);
    setIsApplying(false);
  };

  return (
    <div className="flex h-full flex-col min-h-0">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-3 border-b border-gray-200 px-4 py-3">
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${applied ? 'bg-green-50' : 'bg-amber-50'}`}>
          {applied
            ? <CheckIcon className="h-4 w-4 text-green-600" />
            : <KeyIcon className="h-4 w-4 text-amber-600" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">Credentials</h3>
          <p className="text-xs text-gray-500">
            {mappedCount} of {requirements.length} {applied ? 'applied' : 'configured'}
          </p>
        </div>
        <button
          onClick={fetchCredentials}
          disabled={panelState === 'loading'}
          className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
          title="Refresh credentials"
        >
          <ArrowPathIcon className={`h-4 w-4 ${panelState === 'loading' ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto min-h-0">
        {panelState === 'loading' && requirements.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <SpinnerIcon className="h-5 w-5 text-gray-400" />
          </div>
        ) : panelState === 'error' ? (
          <div className="p-4">
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{errorMessage}</p>
              <button
                onClick={() => openSettingsModal('n8n-connection')}
                className="mt-2 text-xs font-medium text-red-600 hover:text-red-700"
              >
                Check connection in Settings
              </button>
            </div>
          </div>
        ) : (
          <div className="p-3 space-y-3">
            {requirements.map((req) => (
              <CredentialRequirementCard
                key={req.credentialType}
                requirement={req}
                matchingCredentials={availableCredentials.filter(
                  (c) => c.type === req.credentialType
                )}
                instanceUrl={activeInstance.url}
                applied={applied}
                onRefresh={fetchCredentials}
                onRemove={() => handleRemoveCredential(req.credentialType)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer: Apply button */}
      {(mappedCount > 0 || applied) && (
        <div className="flex-shrink-0 border-t border-gray-200 p-3">
          <button
            onClick={handleApply}
            disabled={!allNonOAuth2Mapped || isApplying || applied}
            className={`w-full flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors ${
              applied
                ? 'bg-green-600 cursor-default'
                : isApplying
                ? 'bg-primary-600 opacity-75'
                : 'bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            {applied ? (
              <>
                <CheckIcon className="h-4 w-4" />
                Applied
              </>
            ) : isApplying ? (
              <>
                <SpinnerIcon className="h-4 w-4" />
                Applying...
              </>
            ) : (
              'Apply to Workflow'
            )}
          </button>
          {!allNonOAuth2Mapped && !applied && (
            <p className="mt-1.5 text-center text-xs text-gray-400">
              Configure all required credentials to apply
            </p>
          )}
        </div>
      )}
    </div>
  );
}
